import { FC, useState, useEffect } from 'react';

import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';

import { Events } from '~/constants/events';
import { email as emailRegex } from '~/constants/regex';
import { trackEvent } from '~/helpers/analytics';
import { logError } from '~/helpers/errors';
import GraphQLMutations from '~/networking/graphqlMutations';

const { Typography, Button, TextField } = ToucanComponents;

interface SendExtensionLinkFormInputs {
  email: string;
}

interface ExtensionLinkSendFormProps {
  buttonProps?: {
    [key: string]: any;
  };
  pageName: string;
}

export const ExtensionLinkSendForm: FC<ExtensionLinkSendFormProps> = ({ buttonProps, pageName }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SendExtensionLinkFormInputs>();
  const [submittedEmailAddress, setSubmittedEmailAddress] = useState<string>();
  const [emailExtensionDownloadLink, { data, error: sendEmailError, loading: isSendEmailLoading }] = useMutation(
    GraphQLMutations.EMAIL_EXTENSION_DOWNLOAD_LINK,
    {
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    if (sendEmailError) {
      logError({
        error: sendEmailError,
        extra: {
          submittedEmailAddress,
        },
      });
    }
  }, [sendEmailError]);

  const onSend = async (inputs: SendExtensionLinkFormInputs) => {
    trackEvent({
      name: Events.ExtensionLinkSendCtaClicked,
      properties: {
        pageName,
      },
    });
    const { email } = inputs;
    await emailExtensionDownloadLink({ variables: { email } });
    setSubmittedEmailAddress(email);
  };

  if (data) {
    return (
      <>
        <Typography
          variant="sm"
          isBold
          color={ToucanColors.white}
          css={css`
            margin-top: ${theme.spacing(2)};
          `}
        >
          Your link is on its way!
        </Typography>
        <Typography
          variant="sm"
          color={ToucanColors.white}
          css={css`
            margin-top: ${theme.spacing(0.5)};
          `}
        >
          We sent an email to <strong>{submittedEmailAddress}</strong> with a link to download Toucan next time you’re
          on your desktop.
        </Typography>
      </>
    );
  }

  return (
    <>
      <TextField
        label="Email"
        type="email"
        hasError={!!errors.email}
        helperText={errors.email && errors.email.message}
        css={css`
          margin-top: ${theme.spacing(2)};
          & .MuiFormHelperText-root.Mui-error {
            color: ${ToucanColors.red[100]};
          }
          & .MuiInputBase-root {
            background-color: ${ToucanColors.white};
          }
        `}
        {...register('email', {
          required: `${t('sharedEnterValidEmail')}`,
          pattern: {
            value: emailRegex,
            message: t('sharedEnterValidEmail'),
          },
        })}
      />

      {sendEmailError && (
        <div
          css={css`
            background-color: ${ToucanColors.red[100]};
            padding: ${theme.spacing(1)};
            margin-top: ${theme.spacing(2)};
            border-radius: 4px;
          `}
        >
          <Typography
            variant="xs"
            css={css`
              color: ${ToucanColors.red[300]};
            `}
          >
            Looks like we are having trouble sending you a link. Contact us at{' '}
            <a href="mailto:toucan@babbel.com" target="_blank" rel="noreferrer">
              toucan@babbel.com
            </a>{' '}
            for help.
          </Typography>
        </div>
      )}

      <Button
        onClick={handleSubmit(onSend)}
        isDisabled={isSendEmailLoading}
        {...buttonProps}
        css={css`
          width: 100%;
          margin-top: ${theme.spacing(2)};
        `}
      >
        Send me a link
      </Button>
      <Typography
        variant="xs"
        color={ToucanColors.white}
        css={css`
          margin-top: ${theme.spacing(2)};
        `}
      >
        Toucan is made for your desktop browser. Add your email and we’ll send you a link to download Toucan when you’re
        next on your desktop.
      </Typography>
    </>
  );
};
